import { useState, useEffect, useCallback, useRef } from 'react';
import { ApiException } from '@/lib/api-client';

// Generic hook for API calls
export function useApi<T>(
  apiCall: () => Promise<T>,
  dependencies: unknown[] = []
) {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiException | null>(null);
  // 호출이 겹치면(빠른 페이지 이동, 디바운스 미리보기) 늦게 도착한 이전 응답이 최신 응답을 덮는다 - 마지막 호출만 반영
  const seq = useRef(0);

  const fetchData = useCallback(async () => {
    const id = ++seq.current;
    try {
      setLoading(true);
      setError(null);
      const result = await apiCall();
      if (id === seq.current) setData(result);
    } catch (err) {
      if (id === seq.current) {
        setError(err instanceof ApiException ? err : new ApiException(err instanceof Error ? err.message : String(err)));
      }
    } finally {
      if (id === seq.current) setLoading(false);
    }
  }, dependencies);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return {
    data,
    loading,
    error,
    refetch: fetchData,
  };
}

// Hook for manual API calls (like mutations)
export function useApiMutation<T, P = void>() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiException | null>(null);

  const mutate = useCallback(
    async (apiCall: (params: P) => Promise<T>, params: P): Promise<T> => {
      try {
        setLoading(true);
        setError(null);
        const result = await apiCall(params);
        return result;
      } catch (err) {
        const apiError =
          err instanceof ApiException ? err : new ApiException(String(err));
        setError(apiError);
        throw apiError;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  return {
    mutate,
    loading,
    error,
  };
}
